//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Paper, Tooltip, Typography, Button } from '@mui/material'
import { OpenInNew } from '@mui/icons-material'
import { EthAddr } from '@shared/SchemaTypes'
import React from 'react'

import { useOtChainState, useOtSelector } from '@react/redux/store'
import { PoolFlexOverviewCTO, PoolOverviewCTO } from '@shared/sharedTypes'
import LogoUpload from '@react/components/LogoUpload'

import { buildCompletePoolSummary } from '@react/lender/lenderUtils'
import { LoanStateToString, PoolTypeEnum } from '@shared/Enums'
import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import { logOT } from '@shared/loggingShared'
import { displayName } from '@shared/PoolBaseCTO'
import { DefiCollateralCTO } from '@shared/CollateralSetCTO'

export default function PoolSummary(props: { poolAddr: EthAddr }): React.ReactElement {
  const poolAddr = props.poolAddr
  const { userConnection, chainConfiguration } = useOtChainState()
  const accountAddr = userConnection?.accountAddr
  const poolBaseOverviewCTO = useOtSelector(
    (state) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO] as PoolOverviewCTO | PoolFlexOverviewCTO
  )
  const poolOverviewCTO =
    poolBaseOverviewCTO?.poolType === PoolTypeEnum.Term ? (poolBaseOverviewCTO as PoolOverviewCTO) : null
  const poolFlexOverviewCTO =
    poolBaseOverviewCTO?.poolType === PoolTypeEnum.FlexibleRate ? (poolBaseOverviewCTO as PoolFlexOverviewCTO) : null
  const poolDynamicOverviewCTO =
    poolBaseOverviewCTO?.poolType === PoolTypeEnum.Dynamic ? (poolBaseOverviewCTO as PoolFlexOverviewCTO) : null

  const lenderPoolSummary = poolOverviewCTO && buildCompletePoolSummary(poolOverviewCTO)

  const matureDate = lenderPoolSummary && new Date(lenderPoolSummary.matureEthDate * 1e3)
  logOT.debug('matureDate', matureDate, matureDate?.getDate())

  let lastDayAccrued = ''
  if (poolFlexOverviewCTO?.lastDayAccrued) {
    const d = new Date(poolFlexOverviewCTO.lastDayAccrued * 1e3)
    const year = d.getUTCFullYear() // Get the year in UTC
    const month = d.getUTCMonth() + 1 // Get the month in UTC (0-11, hence adding 1)
    const day = d.getUTCDate() // Get the day in UTC

    lastDayAccrued = `${year}-${month}-${day}`
  }

  const month = matureDate?.toLocaleString('default', { month: 'short' })

  const poolStatus = poolOverviewCTO?.activeLoanState ? LoanStateToString[poolOverviewCTO.activeLoanState.state] : ''

  return (
    <Paper sx={{ marginTop: 4, marginBottom: 4, padding: 8 }}>
      <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: 'row' }}>
        <Box>
          <LogoUpload size={50} addrOrId={poolAddr} />
        </Box>
        <Tooltip
          title={
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid #dadde9',
              }}
            >
              <Typography variant="caption" color="inherit">
                {' '}
                {accountAddr}
              </Typography>
              <Typography variant="overline" margin="dense">
                {displayName(poolBaseOverviewCTO)}
              </Typography>
            </Box>
          }
        >
          <Typography sx={{ alignContent: 'flex-start', width: 100 }} variant="bodyBold" margin="dense">
            {displayName(poolBaseOverviewCTO)}
          </Typography>
        </Tooltip>

        <Box sx={{ alignContent: 'center', flexGrow: 1 }}></Box>
        <Box
          sx={{
            paddingRight: '24px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ marginBottom: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<OpenInNew />}
              size="small"
              onClick={() => {
                const explorerUrl = chainConfiguration?.blockExplorerUrl
                  ? `${chainConfiguration.blockExplorerUrl}/address/${poolAddr}`
                  : ''
                if (explorerUrl) {
                  window.open(explorerUrl, '_blank', 'noopener,noreferrer')
                }
              }}
            >
              View on explorer
            </Button>
            {poolDynamicOverviewCTO &&
              poolDynamicOverviewCTO.collateralSetCTO &&
              poolDynamicOverviewCTO.collateralSetCTO?.isDefi &&
              poolDynamicOverviewCTO.collateralSetCTO.collateral.length > 0 && (
                <Button
                  variant="outlined"
                  startIcon={<OpenInNew />}
                  size="small"
                  onClick={() => {
                    const explorerUrl = (poolDynamicOverviewCTO.collateralSetCTO!.collateral[0] as DefiCollateralCTO)
                      ?.defiNativeURL
                    if (explorerUrl) {
                      window.open(explorerUrl, '_blank', 'noopener,noreferrer')
                    }
                  }}
                >
                  Defi Native Page
                </Button>
              )}
            {poolDynamicOverviewCTO &&
              poolDynamicOverviewCTO.collateralSetCTO &&
              poolDynamicOverviewCTO.collateralSetCTO?.isDefi &&
              poolDynamicOverviewCTO.collateralSetCTO.collateral.length > 0 && (
                <Button
                  variant="outlined"
                  startIcon={<OpenInNew />}
                  size="small"
                  onClick={() => {
                    const explorerUrl = `https://etherscan.io/address/${(poolDynamicOverviewCTO.collateralSetCTO!.collateral[0] as DefiCollateralCTO)?.defiVaultAddr}#readContract`

                    if (explorerUrl) {
                      window.open(explorerUrl, '_blank', 'noopener,noreferrer')
                    }
                  }}
                >
                  Defi Vault Etherscan
                </Button>
              )}
          </Box>

          <Typography sx={{ textAlign: 'left', marginTop: 1 }} variant={'caption'}>
            {poolStatus}
          </Typography>
          {poolFlexOverviewCTO && (
            <>
              <Typography sx={{ textAlign: 'left', marginTop: 1 }} variant={'leadBold'}>
                Last Date Accrued
              </Typography>
              <Typography sx={{ textAlign: 'left', marginTop: 0 }} variant={'bodyBold'}>
                {lastDayAccrued}
              </Typography>
            </>
          )}
          {poolOverviewCTO && lenderPoolSummary && lenderPoolSummary.matureEthDate > 0 && (
            <>
              <Typography sx={{ textAlign: 'left', marginTop: 1 }} variant={'leadBold'}>
                {matureDate?.getDate().toString().padStart(2, '0')}
              </Typography>
              <Typography sx={{ textAlign: 'left', marginTop: 0 }} variant={'leadBold'}>
                {month?.toUpperCase()}
              </Typography>
              <Typography sx={{ textAlign: 'left', marginTop: 0 }} variant={'bodyBold'}>
                {matureDate?.getFullYear()}
              </Typography>
            </>
          )}
        </Box>
      </Box>
    </Paper>
  )
}
